/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Tooltip,
  FormControl,
  MenuItem,
  FormControlLabel,
  styled,
  toggleButtonGroupClasses
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { debounce } from 'lodash';
import Select from '@/components/select/select';
import Android12Switch from '@/components/switches/android-12-switch';
import SearchableSelect from '@/components/searchable-select';
import Typography from '@/components/typography/typography';
import AlignmentControl from './AlignmentControl';

// Styled component with better typing
const StyledToggleButtonGroup = styled(ToggleButtonGroup)<{ item?: number }>(
  ({ theme, item = 4 }) => ({
    display: 'grid',
    borderRadius: 4,
    border: `1px solid #e3e3e3`,
    width: '100%',
    gridTemplateColumns: `repeat(${item}, 1fr)`,
    [`& .${toggleButtonGroupClasses.grouped}`]: {
      margin: theme.spacing(0.5),
      border: 0,
      borderRadius: theme.shape.borderRadius,
      [`&.${toggleButtonGroupClasses.disabled}`]: {
        border: 0,
      },
    },
    [`& .${toggleButtonGroupClasses.middleButton}, & .${toggleButtonGroupClasses.lastButton}`]:
    {
      marginLeft: -1,
      borderLeft: '1px solid transparent',
    },
    [`& .${toggleButtonGroupClasses.selected}`]: {
      backgroundColor: `${theme.palette.action.selected} !important`,
      color: `${theme.palette.text.primary} !important`,
    },
  })
);

// Type definitions
interface TextStyles {
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: string | number;
  textAlign?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
}

interface TextControlsProps {
  attrs: {
    styles?: TextStyles;
    [key: string]: any;
  };
  updateAttrs: (attr: Record<string, any>) => void;
  placeholders?: any[];
  content?: string;
  updateContent?: (content: string) => void;
}

type FormatType = 'bold' | 'italic' | 'underline';

// Constants
const FONT_SIZES = [10, 12, 14, 16, 20, 24, 28, 32] as const;
const LINE_HEIGHTS = [1, 1.2, 1.5, 2] as const;
const FONT_FAMILIES = [
  'Arial',
  'Times New Roman',
  'Roboto',
  'Helvetica',
  'Georgia',
  'Courier New'
] as const;

const DEBOUNCE_DELAY = 300;

const TextControls: React.FC<TextControlsProps> = ({ attrs, updateAttrs, placeholders, content, updateContent }) => {
  const styles = useMemo(() => attrs.styles || {}, [JSON.stringify(attrs.styles)]);

  // Derive format options from current styles instead of separate state
  const formatOptions = useMemo(() => {
    const options: FormatType[] = [];
    if (styles.fontWeight === 'bold') options.push('bold');
    if (styles.fontStyle === 'italic') options.push('italic');
    if (styles.textDecoration === 'underline') options.push('underline');
    return options;
  }, [styles.fontWeight, styles.fontStyle, styles.textDecoration]);

  // Stable update function using useCallback
  const updateStyles = useCallback(
    (newStyles: Partial<TextStyles>) => {
      updateAttrs({
        styles: {
          ...styles,
          ...newStyles
        }
      });
    },
    [styles, updateAttrs]
  );

  // Fixed debounced color update function
  const debouncedColorUpdate = useCallback(
    debounce((color: string) => {
      updateStyles({ color });
    }, DEBOUNCE_DELAY),
    [updateStyles]
  );

  // Format toggle handler
  const toggleFormat = useCallback(
    (format: FormatType) => {
      const isCurrentlyApplied = formatOptions.includes(format);

      const formatStyleMap: Record<FormatType, Partial<TextStyles>> = {
        bold: { fontWeight: isCurrentlyApplied ? 'normal' : 'bold' },
        italic: { fontStyle: isCurrentlyApplied ? 'normal' : 'italic' },
        underline: { textDecoration: isCurrentlyApplied ? 'none' : 'underline' }
      };

      updateStyles(formatStyleMap[format]);
    },
    [formatOptions, updateStyles]
  );

  // Alignment change handler
  const handleAlignmentChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
      if (newAlignment !== null) {
        updateStyles({ textAlign: newAlignment });
      }
    },
    [updateStyles]
  );

  // Color change handler
  const handleColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedColorUpdate(event.target.value);
    },
    [debouncedColorUpdate]
  );

  // Generic style change handlers
  const handleFontFamilyChange = useCallback(
    (event: any) => updateStyles({ fontFamily: event.target.value }),
    [updateStyles]
  );

  const handleFontSizeChange = useCallback(
    (event: any) => updateStyles({ fontSize: Number(event.target.value) }),
    [updateStyles]
  );

  const handleLineHeightChange = useCallback(
    (event: any) => updateStyles({ lineHeight: String(event.target.value) }),
    [updateStyles]
  );

  return (
    <>
      {/* Dynamic Field Toggle */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography type="s5" color="theme.secondary.800">Dynamic text field</Typography>
          <FormControlLabel
            control={
              <Android12Switch
                checked={attrs?.isDynamic || false}
                onChange={(e) => updateAttrs({ isDynamic: e.target.checked })}
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </Box>

        {attrs?.isDynamic && (
          <Box sx={{ mt: 1 }}>
            <SearchableSelect
              value={content?.replace(/\{\{|\}\}/g, '') || ''}
              onChange={(e: any) => updateContent?.(`{{${e.target.value}}}`)}
              options={placeholders?.map((p: any) => ({
                value: p.id || p.field || p.value,
                label: p.label || p.id || p.field || p.name
              })) || []}
              label="Select dynamic field"
            />
          </Box>
        )}
      </Box>

      {/* Font Family Selection */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={styles.fontFamily || ''}
            onChange={handleFontFamilyChange}
            displayEmpty
            label="Select Font"
          >
            {FONT_FAMILIES.map((family) => (
              <MenuItem key={family} value={family}>
                {family}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Font Size and Line Height */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={styles.fontSize || ''}
            onChange={handleFontSizeChange}
            renderValue={(v) => typeof v === 'string' && v.endsWith('px') ? v : `${v}px`}
            label="Size"
            displayEmpty
          >
            {FONT_SIZES.map((size) => (
              <MenuItem key={size} value={size}>
                {size}px
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <Select
            value={styles.lineHeight || ''}
            onChange={handleLineHeightChange}
            renderValue={(v) => `${v}x`}
            label="Line Height"
            displayEmpty
          >
            {LINE_HEIGHTS.map((height) => (
              <MenuItem key={height} value={height}>
                {height}x
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Text Alignment */}
      <AlignmentControl
        alignment={styles.textAlign}
        onAlignmentChange={handleAlignmentChange}
      />

      {/* Format Options */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <StyledToggleButtonGroup item={3}>
          <Tooltip title="Bold">
            <ToggleButton
              value="bold"
              selected={formatOptions.includes('bold')}
              onClick={() => toggleFormat('bold')}
              aria-label="bold"
              size="small"
              sx={{ borderRadius: 1 }}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Italic">
            <ToggleButton
              value="italic"
              selected={formatOptions.includes('italic')}
              onClick={() => toggleFormat('italic')}
              aria-label="italic"
              size="small"
              sx={{ borderRadius: 1 }}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Underline">
            <ToggleButton
              value="underline"
              selected={formatOptions.includes('underline')}
              onClick={() => toggleFormat('underline')}
              aria-label="underline"
              size="small"
              sx={{ borderRadius: 1 }}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </StyledToggleButtonGroup>
      </Box>

      {/* Color Picker */}
      <Box sx={{ mb: 2, width: '100%' }}>
        <TextField
          label="Text Color"
          variant="outlined"
          type="color"
          value={styles.color || '#000000'}
          onChange={handleColorChange}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5, mr: 0.5 }}>
                <ColorLensIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& input[type="color"]': {
              padding: 0,
              margin: '6px 12px 6px 0',
              cursor: 'pointer',
              height: '18px',
              width: 'calc(100% - 12px)',
              border: 'none',
              WebkitAppearance: 'none',
              '&::-webkit-color-swatch-wrapper': {
                padding: 0,
              },
              '&::-webkit-color-swatch': {
                border: '1px solid',
                borderColor: '#e0e0e0',
                borderRadius: '4px',
              },
              '&::-moz-color-swatch': {
                border: '1px solid',
                borderColor: '#e0e0e0',
                borderRadius: '4px',
              }
            }
          }}
        />
      </Box>
    </>
  );
};

export default React.memo(TextControls);